import toString from 'lodash/toString';
import { matchRoutes } from 'react-router-dom';
import { StateCreator } from 'zustand';

import { DEFAULT_AUTH_PATH } from './constants/authPath';
import { routes } from './constants/routes';
import { NonAuthRedirectPath } from './enums/NonAuthRedirectPath';
import { AppRoute } from './types/AppRoute';
import { RouteState } from './types/RouteState';
import { RouteStateDependencies } from './types/RouteStateDependencies';

export const keysToPersist = ['authRedirectPath'];

export const routeStore =
  ({
    location,
    useParameterStore,
  }: RouteStateDependencies): StateCreator<RouteState> =>
  (set, get) => ({
    authRedirectPath: DEFAULT_AUTH_PATH,
    isOnNewUIRoute: true,
    oldUIGateContext: null,

    computed: {
      get authenticatedRoutes(): AppRoute[] {
        const authenticatedRoutes: AppRoute[] = routes
          // filter out the wildcard route that matches all paths.
          // this route is used to redirect the user when attempting to go to a non-existent route.
          .filter((route): boolean => route.path !== '*')

          // filter out non-authenticated routes
          .map((route) => ({
            ...route,
            children: route.children?.filter(
              (childRoute): boolean => !childRoute.isPublicRoute,
            ),
          }));

        return authenticatedRoutes;
      },
      get nonAuthRedirectPath(): string {
        const { intent } = useParameterStore.getState();

        if (
          intent &&
          Object.keys(NonAuthRedirectPath).includes(toString(intent))
        ) {
          return NonAuthRedirectPath[intent];
        }

        return NonAuthRedirectPath.SIGN_IN;
      },
      get signUpRedirectPath(): string {
        const { authRedirectPath } = get();

        if (authRedirectPath === DEFAULT_AUTH_PATH) {
          return '/apply';
        }

        return authRedirectPath;
      },
    },

    // Full page loads go through here, so tests can replace them: a VM test pool
    // can't stub or spy on jsdom's window.location
    assignLocation: (url: string): void => {
      location.assign(url);
    },
    isAuthenticatedRoute: (pathname: string): boolean => {
      const { authenticatedRoutes } = get().computed;

      const routeExists = matchRoutes(authenticatedRoutes, pathname);

      return Boolean(routeExists);
    },
    reloadPage: (): void => {
      location.reload();
    },
    setAuthRedirectPath: (pathname: string): void => {
      const { isAuthenticatedRoute } = get();

      set({
        authRedirectPath: isAuthenticatedRoute(pathname)
          ? pathname
          : DEFAULT_AUTH_PATH,
      });
    },
  });
